// /src/main.ts

import { initNativeFeatures } from './nativeFeatures';
import { initShortcutBridge, bindHashViewRenderer } from './shortcut-router';
import { loadTodaySuggestion } from './suggestions';
import { saveTodaySuggestionForWidget } from './native/widgetSync';
import './native/voice-summary';
// ❌ no direct DayCounter import here anymore
// import { DayCounter } from './native/day-counter-bridge';
// import { reloadAllWidgets } from './native/widgets-reload';
import { wireDayCounter, ensureAppGroupStamp, reloadDayCounterWidgets } from './native/day-counter-sync';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { SplashScreen } from '@capacitor/splash-screen';

// NEW: auto-dim after inactivity
import { AutoDim, wireAutoDim } from './native/auto-dim';

// NEW: goodbye notifications
import { initGoodbyeNotifications, DEFAULT_GOODBYES } from './native/goodbye-notifier';

// ✅ Boot marker
console.log('[Trauvo] main.ts loaded @', new Date().toISOString());
performance.mark('trauvo:main-loaded');

// Expose AutoDim globally for console & non-module access (Safari-friendly)
(globalThis as any).AutoDim = AutoDim;
(window as any).AutoDim = AutoDim;
(self as any).AutoDim = AutoDim;
console.log('[Trauvo] AutoDim global exposed =', typeof (globalThis as any).AutoDim);

// Handy console helpers
(window as any).AUTO_DIM_CONFIGURE = (s = 30, l = 0.5) =>
  AutoDim.configure({ idleSeconds: s, dimLevel: l });
(window as any).AUTO_DIM_PING = () => AutoDim.ping();

/**
 * Normalize + push today's suggestion into:
 * - Your UI (if needed)
 * - The native/widget sync layer
 */
export async function handleSuggestionResponse(data: any) {
  if (!data) return;

  try {
    // 1) Push a minimal payload to the widget sync layer
    await saveTodaySuggestionForWidget({
      id: String(data.activity_id ?? data.id ?? ''),
      title: data.title || data.name || '',
      subtitle: data.short_description || data.location || '',
    });
  } catch (err) {
    console.warn('[Trauvo] Failed to sync suggestion to widget:', err);
  }

  // 2) Normalize and expose the current activity for native helpers & voice summary
  try {
    const w = window as any;

    const normalized = {
      id: String(data.activity_id ?? data.id ?? ''),
      title: data.title || data.name || '',
      location_name: data.location_name || data.location || data.venue || '',
      city: data.city || data.region || '',
      lat: Number(data.lat ?? data.latitude ?? data.lng_lat?.[0] ?? 0),
      lng: Number(data.lng ?? data.longitude ?? data.lng_lat?.[1] ?? 0),
      short_summary:
        data.short_summary ||
        data.short_description ||
        data.reason ||
        data.subtitle ||
        '',
    };

    w.currentActivity = normalized;
    w.lastSuggestedActivity = normalized;

    console.debug('[Trauvo] currentActivity set for voice summary:', normalized);
  } catch (err) {
    console.warn('[Trauvo] Failed to expose currentActivity for voice summary:', err);
  }
}

(async function bootstrap() {
  // 1) Native features (Capacitor, Preferences, etc.)
  try {
    await initNativeFeatures();
  } catch (err) {
    console.warn('[Trauvo] initNativeFeatures failed:', err);
  }

  // 1.0) Auto-dim after inactivity (dims to 50% after 30s; tweak as you like)
  wireAutoDim({ idleSeconds: 30, dimLevel: 0.5 });

  // 1.1) Ensure App Group install_ts exists (for Day counter widget), then nudge widgets
  try {
    await ensureAppGroupStamp();      // uses DayCounter.ensureInstallTs() under the hood (native only)
    await reloadDayCounterWidgets();  // uses DayCounter.reloadDayCounterWidget()/reloadAllWidgets() if native
  } catch (err) {
    console.warn('[Trauvo] DayCounter ensure/reload failed:', err);
  }

  // 1.2) Update in-app Day banner (fills #day-banner-message with "Day X of adventures!")
  wireDayCounter();

  // 1.3) Keep things fresh on foreground (after midnight, etc.)
  App.addListener('appStateChange', async ({ isActive }) => {
    if (!isActive) return;
    try {
      await ensureAppGroupStamp();
      await reloadDayCounterWidgets();
      await wireDayCounter();
    } catch (e) {
      console.warn('[Trauvo] Foreground refresh failed:', e);
    }
  });

  // 1.4) 👋 Goodbye notifications
  initGoodbyeNotifications({
    delaysMinutes: [5, 2, 8, 10, 5],
    messages: DEFAULT_GOODBYES,
    dailyCap: 2,
    scheduleCount: 1,
  });

  // 1.5) Optional: simple native log (no PingPlugin)
  if (Capacitor.isNativePlatform()) {
    console.log('[Trauvo] Running inside native container:', Capacitor.getPlatform());
  }

  // 2) Shortcut / deep link bridge
  try {
    await initShortcutBridge();
  } catch (err) {
    console.warn('[Trauvo] initShortcutBridge failed:', err);
  }

  // 3) Hash-based view switching
  bindHashViewRenderer({
    routes: {
      '/nearby': 'view-nearby',
      '/favorites': 'view-favorites',
      '/': 'view-home',
    },
    containerSelector: 'main',
  });

  // 3.5) Hide the native splash once the main UI is wired up
  try {
    if (Capacitor.isNativePlatform && Capacitor.isNativePlatform()) {
      await SplashScreen.hide();
    }
  } catch (err) {
    console.warn('[Trauvo] SplashScreen.hide failed or skipped:', err);
  }

  // 4) On load: fetch today's suggestion → push to UI + widget
  try {
    const data = await loadTodaySuggestion(); // ensure this returns the suggestion object
    if (data) {
      await handleSuggestionResponse(data);
    }
  } catch (err) {
    console.warn('[Trauvo] Failed to load today suggestion:', err);
  }

  // 5) Other startup hooks:
  //    initSuggestionFlow();
  //    hydrateUserSession();
  //    attachGlobalListeners();
})();

// Wire voice summary icon beside Download Guide
// This assumes ./native/voice-summary has already attached helpers on window

async function openNativeOrWebProfile() {
  try {
    const isNative =
      (window as any).Capacitor &&
      typeof (window as any).Capacitor.isNativePlatform === 'function' &&
      (window as any).Capacitor.isNativePlatform();

    const isCapacitorUA = /Capacitor/i.test(navigator.userAgent || '');

    if (isNative || isCapacitorUA) {
      const plugin = (window as any).Capacitor?.Plugins?.NativeProfile;
      if (plugin && typeof plugin.open === 'function') {
        await plugin.open();
        return;
      }
    }
  } catch (e) {
    console.warn('[profile] Native open failed, falling back to web modal', e);
  }

  // Web fallback: open existing profile modal
  const modal = document.getElementById('profile-modal');
  if (modal) {
    modal.classList.add('active');
    document.body.classList.add('no-scroll');
  }
}

(window as any).openNativeOrWebProfile = openNativeOrWebProfile;

document.addEventListener('DOMContentLoaded', () => {
  const voiceSummaryBtn = document.getElementById('voice-summary-btn');
  if (!voiceSummaryBtn) return;

  voiceSummaryBtn.addEventListener('click', async () => {
    try {
      const w = window as any;
      const activity = w.currentActivity || w.lastSuggestedActivity || null;

      if (!activity) {
        console.warn('[voice-summary] No activity found on window.currentActivity / window.lastSuggestedActivity');
        return;
      }

      if (typeof w.playActivityVoiceSummary === 'function') {
        await w.playActivityVoiceSummary(activity);
      } else {
        console.warn('[voice-summary] playActivityVoiceSummary is not on window');
      }
    } catch (err) {
      console.warn('[voice-summary] Failed to play summary from voiceSummaryBtn click', err);
    }
  });
});